/**
 * Pure geometry for the square pan/zoom image cropper. Keeps the
 * viewport↔source-pixel mapping testable and out of the React component.
 *
 * Model: a square viewport of `displaySize` px. At zoom 1 the image is scaled
 * to *cover* the square (shortest side fills it); zoom multiplies that. The
 * image is centred, then nudged by (offsetX, offsetY) px from dragging. Offsets
 * are clamped so the image always covers the viewport (no empty gutters).
 */

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Scale at which the image just covers the square viewport (zoom = 1). */
export function coverScale(displaySize: number, imgW: number, imgH: number): number {
  return displaySize / Math.min(imgW, imgH);
}

/** Max pan offset (px, per axis) that still keeps the rendered image covering
 *  the viewport. Symmetric: allowed range is [-max, +max]. */
export function maxOffset(displaySize: number, renderedSize: number): number {
  return Math.max(0, (renderedSize - displaySize) / 2);
}

export interface CropView {
  displaySize: number;
  imgW: number;
  imgH: number;
  zoom: number;
  offsetX: number;
  offsetY: number;
}

/** The source-image square (in source pixels) currently framed by the viewport. */
export function computeCropRect(v: CropView): { sx: number; sy: number; sSide: number } {
  const scale = coverScale(v.displaySize, v.imgW, v.imgH) * v.zoom;
  const renderedW = v.imgW * scale;
  const renderedH = v.imgH * scale;
  const left = (v.displaySize - renderedW) / 2 + v.offsetX;
  const top = (v.displaySize - renderedH) / 2 + v.offsetY;
  const sSide = v.displaySize / scale;
  return { sx: -left / scale, sy: -top / scale, sSide };
}

/** Clamp a candidate offset pair for the given zoom so the image keeps covering. */
export function clampOffsets(
  displaySize: number,
  imgW: number,
  imgH: number,
  zoom: number,
  offsetX: number,
  offsetY: number,
): { x: number; y: number } {
  const scale = coverScale(displaySize, imgW, imgH) * zoom;
  const mx = maxOffset(displaySize, imgW * scale);
  const my = maxOffset(displaySize, imgH * scale);
  // `+ 0` normalises -0 (from clamping a negative into a zero range) to 0.
  return { x: clamp(offsetX, -mx, mx) + 0, y: clamp(offsetY, -my, my) + 0 };
}
